import React, { useState, useEffect, useRef } from 'react';
import { Search, X } from 'lucide-react';
import { Input } from '../ui/input';
import { Card } from '../ui/card';
import { useNavigate } from 'react-router-dom';
import { useProducts } from '../../hooks/useData';

export const SearchWithAutocomplete = ({ className }) => {
  const [query, setQuery] = useState('');
  const [showResults, setShowResults] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const searchRef = useRef(null);
  const navigate = useNavigate();
  const { products } = useProducts();

  // Filter products based on search query
  const searchResults = query.length > 1
    ? products
        .filter(p =>
          p.name.toLowerCase().includes(query.toLowerCase()) ||
          p.brand.toLowerCase().includes(query.toLowerCase()) ||
          (p.description || '').toLowerCase().includes(query.toLowerCase())
        )
        .slice(0, 5)
    : [];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (query.trim()) {
      navigate(`/shop?search=${encodeURIComponent(query)}`);
      setShowResults(false);
      setQuery('');
    }
  };

  const handleProductClick = (productId) => {
    navigate(`/product/${productId}`);
    setShowResults(false);
    setQuery('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFocusedIndex(prev => Math.min(prev + 1, searchResults.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFocusedIndex(prev => Math.max(prev - 1, -1));
    } else if (e.key === 'Enter' && focusedIndex >= 0) {
      e.preventDefault();
      handleProductClick(searchResults[focusedIndex].id);
    }
  };

  return (
    <div ref={searchRef} className={`relative ${className}`}>
      <form onSubmit={handleSearch}>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            type="search"
            placeholder="Search products..."
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setShowResults(true);
              setFocusedIndex(-1);
            }}
            onFocus={() => query && setShowResults(true)}
            onKeyDown={handleKeyDown}
            className="pl-10 pr-10"
          />
          {query && (
            <button
              type="button"
              onClick={() => {
                setQuery('');
                setShowResults(false);
              }}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </form>

      {/* Autocomplete Dropdown */}
      {showResults && searchResults.length > 0 && (
        <Card className="absolute top-full left-0 right-0 mt-2 z-50 max-h-96 overflow-y-auto">
          <div className="p-2">
            {searchResults.map((product, index) => (
              <button
                key={product.id}
                onClick={() => handleProductClick(product.id)}
                className={`w-full flex items-center gap-3 p-3 rounded-lg transition-smooth text-left ${
                  index === focusedIndex ? 'bg-muted' : 'hover:bg-muted'
                }`}
              >
                <div className="w-12 h-12 rounded overflow-hidden bg-muted flex-shrink-0">
                  {product.images && product.images[0] ? (
                    <img
                      src={product.images[0]}
                      alt={product.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <span className="text-xs text-muted-foreground">NC</span>
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm line-clamp-1">{product.name}</p>
                  <p className="text-xs text-muted-foreground">{product.brand}</p>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-primary">
                    ${product.price}
                  </p>
                </div>
              </button>
            ))}
          </div>
          {searchResults.length === 5 && (
            <div className="border-t p-2">
              <button
                onClick={handleSearch}
                className="w-full text-sm text-primary hover:underline text-center py-2"
              >
                View all results for "{query}"
              </button>
            </div>
          )}
        </Card>
      )}
    </div>
  );
};